/**
 * Display helpers for the Marketing section — the ONE copy of each label, so
 * the campaign list, the builder, the report and the CRM history panel cannot
 * word the same state differently. Nothing here fetches or writes.
 *
 * Contract: `docs/api/marketing.md` in emerge-backend-app.
 */
import type { BadgeTone } from '@/components/ui/Badge';
import type {
  CampaignCondition,
  MarketingCampaignStatus,
  MarketingSkipReason,
  MarketingStopReason,
  MarketingSuppressionReason,
} from './types';

export const CAMPAIGN_STATUS_LABEL: Record<MarketingCampaignStatus, string> = {
  draft: 'Draft',
  pending_approval: 'Awaiting approval',
  scheduled: 'Scheduled',
  sending: 'Sending',
  paused: 'Paused',
  completed: 'Completed',
  cancelled: 'Cancelled',
  failed: 'Failed',
};

const CAMPAIGN_STATUS_TONE: Record<MarketingCampaignStatus, BadgeTone> = {
  draft: 'neutral',
  pending_approval: 'warning',
  scheduled: 'info',
  sending: 'accent',
  paused: 'warning',
  completed: 'success',
  cancelled: 'neutral',
  failed: 'danger',
};

/** Tone is never the only signal — always render the label beside it. */
export function campaignStatusTone(status: MarketingCampaignStatus): BadgeTone {
  return CAMPAIGN_STATUS_TONE[status] ?? 'neutral';
}

export const SKIP_REASON_LABEL: Record<MarketingSkipReason, string> = {
  suppressed: 'Suppressed',
  opted_out: 'Opted out',
  frequency_cap: 'Frequency cap reached',
  invalid_email: 'Invalid email address',
  cancelled: 'Campaign cancelled',
  condition_not_met: 'Step condition not met',
};

export const STOP_REASON_LABEL: Record<MarketingStopReason, string> = {
  replied: 'Replied',
  unsubscribed: 'Unsubscribed',
  bounced: 'Bounced',
  suppressed: 'Suppressed',
  cancelled: 'Campaign cancelled',
};

export const SUPPRESSION_REASON_LABEL: Record<MarketingSuppressionReason, string> = {
  unsubscribed: 'Unsubscribed',
  hard_bounce: 'Hard bounce',
  complaint: 'Spam complaint',
  manual: 'Added manually',
};

export const CONDITION_LABEL: Record<CampaignCondition, string> = {
  all: 'Everyone still in the sequence',
  not_opened: 'Did not open the previous email',
  not_clicked: 'Did not click in the previous email',
  opened: 'Opened the previous email',
  clicked: 'Clicked in the previous email',
};

/**
 * A rate as a percentage, one decimal place. `null` (nothing sent, so no
 * denominator) renders as an em dash — never as `0.0%`, which would read as a
 * measured failure.
 *
 * Accepts the rate as a FRACTION (0..1) — the shape the report's `openRate` /
 * `clickRate` and the overview's rates use.
 */
export function percent(n: number | null): string {
  if (n === null || !Number.isFinite(n)) return '—';
  return `${(n * 100).toFixed(1)}%`;
}
